import { FieldDetails } from "shared/api/apiModels";
import FieldDetailsApi from "shared/api/FieldDetailsApi";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { setCurrentFieldDetails } from "features/panorama/details-slice";
import {
  addKrpanoHotspot,
  addKrpanoHotspotLayer,
  safelyParseComma,
} from "shared/helpers/krpano";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";
import { publicPath } from "shared/helpers/publicPath";
import { useEffect, useState } from "react";

type UseGetFieldDetails = (
  krpanoLoaded: boolean,
  farmLoaded: boolean
) => boolean;

/**
 *
 * @param krpanoLoaded see `useEmbedPano`
 * @param farmLoaded see `useGetFarmDetails`
 * @returns `fieldLoaded` - `true` if field details are loaded
 */
export const useGetFieldDetails: UseGetFieldDetails = (
  krpanoLoaded,
  farmLoaded
) => {
  const dispatch = useAppDispatch();
  const [fieldLoaded, setFieldLoaded] = useState<boolean>(false);
  const guid = useAppSelector((state) => state.panorama.guid);
  const currentField = useAppSelector((state) => state.panorama.currentField);
  const refreshCount = useAppSelector((state) => state.panorama.refreshCount);
  const neighbouringFields =
    useAppSelector((state) => state.panorama.farmDetails?.fields)
      ?.map((field) => field.fid)
      .toString() || "";

  useEffect(() => {
    const getFieldDetails = async (panoGuid: string, fieldId: string) => {
      const response = await FieldDetailsApi.getFieldDetails({
        currentFieldID: fieldId,
        panoramaGuid: panoGuid,
        listSphereFieldIDs: neighbouringFields,
      });
      if (response.success && response.data) {
        dispatch(setCurrentFieldDetails(response.data.current_field));
        parseFieldDetails(response.data, panoGuid);
      } else {
        console.error(response.errors);
      }

      setFieldLoaded(true);
    };
    if (
      krpanoLoaded &&
      farmLoaded &&
      guid &&
      notNullNorUndefined(currentField)
    ) {
      getFieldDetails(guid, currentField);
    }
  }, [
    krpanoLoaded,
    farmLoaded,
    guid,
    currentField,
    neighbouringFields,
    refreshCount,
    dispatch,
  ]);

  return fieldLoaded;
};

/**
 * This seems obsolete, but is left here for backwards compatibility of old API
 * responses
 */
const parseImageURL = (imageURL: string) => {
  if (
    imageURL === "Panos/placeholder" ||
    imageURL === "Panos/placeholder.png" ||
    imageURL === "Panos/blank-placeholder" ||
    imageURL === "Panos/blank-placeholder.png"
  ) {
    // NOTE: This may only be required for the local server
    if (imageURL === "Panos/placeholder") {
      return publicPath(`krpano/Panos/placeholder.jpeg`);
    } else if (imageURL === "Panos/blank-placeholder") {
      return publicPath(`krpano/Panos/blank-placeholder.png`);
    }
    return publicPath(`krpano/${imageURL}`);
  } else {
    return imageURL;
  }
};

const parseFieldDetails = (details: FieldDetails, panoGuid: string) => {
  window.krpano.image.reset();

  window.krpano.preview = {
    url: parseImageURL(details.current_field.image_preview_uri),
    type: "sphere",
  };
  window.krpano.image.sphere = {
    url: parseImageURL(details.current_field.image_uri),
  };
  const loadpanoimageflags = window.krpano.scene_load.loadpanoimage_flags;
  const blend = window.krpano.scene_load.blend;

  window.krpano.actions.loadpanoimage(loadpanoimageflags, blend);

  // SET_SCENE_NAME
  window.krpano.xml.scene = details.current_field.name;
  // const currentFieldPropertiesToExclude = [
  //   "name",
  //   "fid",
  //   "scoutPoints",
  //   "boundary",
  //   "image_uri",
  //   "image_preview_uri",
  //   "b_trial_plot",
  // ];
  const isTrialPlot = details.current_field.b_trial_plot;

  // NOTE: THIS LOOP HERE REMOVED THE SCENE DATA SOMEHOW
  // for (const [key, value] of Object.entries(details.current_field)) {
  //   if (!currentFieldPropertiesToExclude.includes(key)) {
  //     window.krpano.set(`scene[get(xml.scene)].${key}`, value);
  //   }
  // }

  // Add data to current scene
  // NOTE: This requires the scene name to be set first
  // SEE: SET_SCENE_NAME
  const properitesToInclude: (keyof typeof details.current_field)[] = [
    "altitude",
    "drone_altitude",
    "fid",
    "lat",
    "lanscape_ath",
    "lng",
    "orientation_angle",
    "portrait_ath",
    "zoom_level",
  ];
  const scene = window.krpano.scene.getItem(window.krpano.xml.scene);
  if (scene) {
    properitesToInclude.forEach((property) => {
      scene[property] = details.current_field[property];
    });
    scene["panoGuid"] = panoGuid;
  }

  // <!-- Boundary -->
  const boundaryLine = addKrpanoHotspot(
    "boundary_line",
    {
      // Group
      group: "boundary_line",
    },
    "Field_Boundary_Line"
  );
  // Create Boundary Line
  addKrpanoHotspotLayer(boundaryLine, details.current_field.boundary);

  // <!-- Scout points / POI -->
  details.current_field.scoutPoints.forEach(function (scoutPoint) {
    const scoutPointName = scoutPoint.name;
    addKrpanoHotspot(
      scoutPointName,
      {
        // ATH/ATV
        ath: scoutPoint.ath,
        atv: scoutPoint.atv,
        // POI Number
        poi_number: scoutPoint.poi_Number,
        // Type
        type: "image",
        // UI/UX Behaviours
        onclick: `Load_POI(null, ${scoutPoint.poi_Number});`,
        onover: `set(global.onPoi, ${scoutPointName});`,
        onout: "set(global.onPoi, null);",
        // Group
        group: "poi_spot",
      },
      isTrialPlot ? "POI_TrialPlot_Marker" : "POI_Marker"
    );
  });

  // <!-- Neighbouring fields / Scene markers -->
  details.neighbouring_fields.forEach((neighbouringField) => {
    const neighbouringFieldName = `${
      neighbouringField.scene_name
    }-${safelyParseComma(neighbouringField.name)}`;

    addKrpanoHotspot(
      neighbouringFieldName,
      {
        type: "image",
        // UI/UX Behaviours
        onhover: `showtext(${safelyParseComma(
          neighbouringField.title
        )}, Field_Marker_Text);`,
        onup: `skin_loadscene(${neighbouringField.scene_name}, get(scene_load.blend), 0, false);`,
        // Field Data
        altitude: neighbouringField.altitude,
        ath: neighbouringField.ath,
        atv: neighbouringField.atv,
        fid: neighbouringField.fid,
        lat: neighbouringField.lat,
        lng: neighbouringField.lng,
        orientation_angle: neighbouringField.orientation_angle,
        // Group
        group: "field_marker",
      },
      "Field_Marker"
    );
  });

  // Scale hotspots after adding them
  window.krpano.call("scale_hotspots();");
};
